/**
 * The Metals page tab "Adăugare după tip metal": the inventories still holding metal to add to
 * stock, split into the four precious metals, so the operator can add an inventory one metal at a
 * time. The server lists only what is left to add; see `GET /api/metals/incoming/by-metal`.
 */

export const METAL_CATEGORIES = ['AUR', 'ARGINT', 'PLATINA', 'PALADIU'] as const;

export type MetalCategory = (typeof METAL_CATEGORIES)[number];

/** One inventory date of one metal with what is left to add of it. */
export interface MetalInventoryDate {
    date: string;
    items: number;
    grams: number;
}

export interface MetalCategoryDates {
    metalType: MetalCategory;
    dates: MetalInventoryDate[];
}

/** One pending metal object of an inventory date. */
export interface MetalInventoryItem {
    itemId: number;
    contract_number: string;
    client_name: string | null;
    metal_type: string;
    purity: number | string;
    quantity_grams: number | string;
    value_ron: number | string;
}

export interface MetalDateSelection {
    metalType: MetalCategory;
    date: string;
}

/** The ticked dates of each metal. */
export type MetalSelections = Partial<Record<MetalCategory, string[]>>;

/** The translation key of a metal's column heading. */
export const metalCategoryLabelKey = (metalType: MetalCategory): string =>
    `metals.byMetal.category.${metalType}`;

/** The expanded-row cache key of one date of one metal. */
export const metalDateKey = (metalType: MetalCategory, date: string): string =>
    `${metalType}|${date}`;

/** Every column of the response, in the fixed order, even one the server left out. */
export const orderMetalCategories = (categories: MetalCategoryDates[]): MetalCategoryDates[] =>
    METAL_CATEGORIES.map(metalType => ({
        metalType,
        dates: categories.find(category => category.metalType === metalType)?.dates ?? [],
    }));

/** The ticked dates as the request body lists them, metal by metal. */
export const toMetalDateSelections = (selections: MetalSelections): MetalDateSelection[] =>
    METAL_CATEGORIES.flatMap(metalType =>
        (selections[metalType] ?? []).map(date => ({ metalType, date })),
    );

/** Drops the ticks of dates no longer listed (added meanwhile, from here or elsewhere). */
export const pruneMetalSelections = (
    selections: MetalSelections,
    categories: MetalCategoryDates[],
): MetalSelections => {
    const pruned: MetalSelections = {};
    for (const { metalType, dates } of categories) {
        const listed = new Set(dates.map(entry => entry.date));
        const kept = (selections[metalType] ?? []).filter(date => listed.has(date));
        if (kept.length > 0) {
            pruned[metalType] = kept;
        }
    }
    return pruned;
};
